var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { effectiveTimeZone, formatVoterDateTimeZone, formatVotingPortalDateTime, primaryTimeZone, } from "@sequentech/ui-core";
import { getEndDateEntry, hasDate, sameWallClock } from "@sequentech/ui-essentials";
/** Live execution dates overlay display metadata, leaving the signed publication unchanged. */
export var currentElectionDates = function (published, status, isKiosk) {
    var period = isKiosk ? status === null || status === void 0 ? void 0 : status.kiosk_voting_period_dates : status === null || status === void 0 ? void 0 : status.voting_period_dates;
    return period ? __assign(__assign({}, published), period) : published;
};
var hasTimeZones = function (event) { var _a, _b; return !!((_b = (_a = event === null || event === void 0 ? void 0 : event.timezones) === null || _a === void 0 ? void 0 : _a.primary) === null || _b === void 0 ? void 0 : _b.trim()); };
/**
 * The zones of one ballot (VOTE-LIFECYCLE design §3, §8), by the sequent-core
 * rule. The event's `timezones` come from the published event (the
 * publication files or `election_event_config.json`), the election's
 * `timezone` from the published election; the ballot style's copies stand in
 * when those aren't loaded.
 *
 * Nothing when neither copy of the event configures timezones (an event
 * published before them): its dates then show as before, plain times in the
 * browser's zone with no zone named.
 */
export var ballotTimeZones = function (event, election, ballotStyle) {
    var eventZones = hasTimeZones(event)
        ? event
        : hasTimeZones(ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.election_event_presentation)
            ? ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.election_event_presentation
            : undefined;
    if (!eventZones) {
        return undefined;
    }
    var electionZone = (election === null || election === void 0 ? void 0 : election.timezone) ? election : ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.election_presentation;
    return {
        timeZone: effectiveTimeZone(eventZones, electionZone),
        closeTimeZone: primaryTimeZone(eventZones),
    };
};
/**
 * A time on a ballot's screens (the Ballot Locator's logs): in the Post's zone
 * with the zone named, or, without zones, as before.
 */
export var formatBallotTime = function (instant, _a) {
    var event = _a.event, zones = _a.zones, t = _a.t, lang = _a.lang;
    return zones
        ? formatVoterDateTimeZone(instant, zones.timeZone, {
            t: t,
            lang: lang,
            formatDateTime: function (date, zone) { return formatVotingPortalDateTime(date, event, lang, zone); },
        })
        : formatVotingPortalDateTime(instant, event, lang);
};
/**
 * The ballot list's closed message, once the event's voting is closed (the
 * caller checks that) and every listed ballot's close has passed: the latest
 * close, including a recorded manual stop before the scheduled deadline,
 * in the zone it was scheduled in (the primary for the common close)
 * with the Post's time in brackets (`electionSelectionScreen.votingClosedAt`),
 * or once when both read the same (`electionSelectionScreen.votingClosedOn`).
 * Nothing while a close is still ahead (a Post's test voting closing before its
 * election opens isn't "voting closed"), and nothing for an event without
 * timezones, whose list shows no closed message, as before.
 */
export var votingClosedMessage = function (_a) {
    var _b;
    var ballots = _a.ballots, now = _a.now, t = _a.t, lang = _a.lang, formatDateTime = _a.formatDateTime;
    var latest;
    for (var _i = 0, ballots_1 = ballots; _i < ballots_1.length; _i++) {
        var ballot = ballots_1[_i];
        var entry = getEndDateEntry(ballot.electionDates, now);
        if (!entry || !hasDate(entry.date)) {
            continue;
        }
        if (new Date(entry.date) > now) {
            return undefined;
        }
        if (!latest || new Date(entry.date) > new Date(latest.end)) {
            latest = { end: entry.date, endZone: entry.timeZone, zones: ballot.zones };
        }
    }
    if (!(latest === null || latest === void 0 ? void 0 : latest.zones)) {
        return undefined;
    }
    var end = latest.end, zones = latest.zones;
    var closeZone = (_b = latest.endZone) !== null && _b !== void 0 ? _b : zones.closeTimeZone;
    var options = {
        t: t,
        lang: lang,
        formatDateTime: function (instant, zone) {
            return formatDateTime(instant.toISOString(), zone);
        },
    };
    var close = formatVoterDateTimeZone(end, closeZone, options);
    if (sameWallClock(end, zones.timeZone, closeZone)) {
        return t("electionSelectionScreen.votingClosedOn", { close: close });
    }
    return t("electionSelectionScreen.votingClosedAt", {
        close: close,
        localClose: formatVoterDateTimeZone(end, zones.timeZone, options),
    });
};
